export interface KnowledgeBaseDocument {
  type: "file" | "url" | "text";
  access_info: {
    is_creator: boolean;
    creator_name: string;
    creator_email: string;
    role: string;
  };
  dependent_agents: DependentAgent[];
  id: string;
  metadata: {
    created_at_unix_secs: number;
    last_updated_at_unix_secs: number;
    size_bytes: number;
  };
  name: string;
  prompt_injectable: boolean;
  is_public?: boolean;
  [key: string]: any;
}

export interface DependentAgent {
  type: "available" | "unknown";
  id: string;
  access_level?: string;
  created_at_unix_secs?: number;
  name?: string;
  branding?: {
    primaryColor?: string;
    secondaryColor?: string;
    logoUrl?: string;
    customAvatarUrl?: string;
    gradientEnabled?: boolean;
    gradientAngle?: number;
    [key: string]: any;
  };
  category?: string;
  elevenlabs_id?: string;
  is_active?: boolean;
}

export interface DocumentWithMetadata {
  id: string;
  name: string;
  is_active?: boolean | null;
  is_public: boolean;
  is_personal?: boolean | null;
  el_doc_id?: string | null;
  user_id?: string | null;
  folder_id?: string | null;
  source_type?: string | null;
  status?: string | null;
  consumer_genie_id?: string | null;
  optimized_path?: string | null;
  parsed_path?: string | null;
  personal_notes?: string | null;
  source_path?: string | null;
  metadata?: {
    pdf_size?: number;
    type?: string;
    total_pages?: number;
    contentType?: string;
    description?: string;
    [key: string]: any;
  } | null;
  created_at?: string | null;
  updated_at?: string | null;
  owner?: {
    id?: string;
    full_name?: string;
    email?: string;
    [key: string]: any;
  } | null;
  folder?: {
    id: string;
    name: string;
    color?: string;
    icon?: string;
    position?: number;
  } | null;
  dependent_agents?: DependentAgent[];
}

export interface KnowledgeBase {
  id: string;
  name: string;
  type: "file" | "url" | "text";
}

export interface CreateDocumentRequest {
  content: string | File;
  name?: string;
}

export interface CreateDocumentResponse {
  id: string;
  name: string;
  prompt_injectable: boolean;
}

export interface ListKnowledgeBaseOptions {
  cursor?: string;
  page_size?: number;
  search?: string;
  show_only_owned_documents?: boolean;
  types?: Array<"file" | "url" | "text">;
  use_typesense?: boolean;
}

export interface DocumentFilters {
  searchTerm?: string;
  selectedUsername?: string;
  selectedPrivacy?: string;
  folderId?: string; // 'null' = uncategorized, undefined = all
  agentId?: string; // Filter by agent id, 'all' = no filter
  selectedProcessingStatus?: string; // Filter by document processing status (pending, completed, failed)
  selectedSourceType?: string; // Filter by document source type (pdf_upload, document_upload, etc.)
  selectedActivityStatus?: string; // Filter by is_active (active, inactive)
  readyOnly?: boolean; // Filter to documents that have el_doc_id set (ready to attach)
}

export interface ListDocument {
  id: string;
  name: string;
  el_doc_id: string;
  is_public: boolean;
  created_at: string;
  owner?: {
    id: string;
    full_name: string;
    email: string;
  };
}

export enum DocumentSource {
  Audio = "audio_transcription",
  Video = "video_transcription",
  Pdf = "pdf_upload",
  Upload = "document_upload",
  Website = "website",
  YouTube = "youtube",
  Note = "notes",
  Scrape = "website-scrape",
  QuickResearch = "quick-research",
  DeepResearch = "deep-research",
}

export const DocumentTypeIconMap = {
  // Document sources
  audio_transcription: { icon: "Mic", label: "Audio", color: "text-purple-600" },
  video_transcription: { icon: "Video", label: "Video", color: "text-purple-600" },
  pdf_upload: { icon: "FileText", label: "PDF", color: "text-red-600" },
  document_upload: { icon: "FileText", label: "Document", color: "text-blue-600" },
  image_upload: { icon: "Image", label: "Image", color: "text-cyan-600" },
  website: { icon: "Globe", label: "Website", color: "text-green-600" },
  "website-scrape": { icon: "ScanText", label: "Scraped Website", color: "text-orange-600" },
  youtube: { icon: "Play", label: "YouTube", color: "text-red-500" },
  notes: { icon: "StickyNote", label: "Notes", color: "text-yellow-600" },
  "quick-research": { icon: "Search", label: "Research", color: "text-blue-600" },
  "deep-research": { icon: "Binoculars", label: "Deep Research", color: "text-indigo-600" },
  // Written by the Genie from its own sources, not learned from — see
  // GENIE_DOCUMENT_SOURCE_TYPE. Labelled distinctly so it is never mistaken for
  // a source in the workspace-wide document list.
  "genie-document": { icon: "FileText", label: "Branded Document", color: "text-genie-primary" },

  // MIME types fallback
  "application/pdf": { icon: "FileText", label: "PDF", color: "text-genie-primary" },
  "text/plain": { icon: "AlignLeft", label: "Text", color: "text-gray-600" },
  "text/html": { icon: "Code", label: "HTML", color: "text-orange-600" },
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document": {
    icon: "FileText",
    label: "Word",
    color: "text-blue-700",
  },
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": {
    icon: "Table",
    label: "Excel",
    color: "text-green-700",
  },
  "application/vnd.google-apps.document": { icon: "FileText", label: "Google Docs", color: "text-blue-600" },
  "application/vnd.google-apps.spreadsheet": { icon: "Table", label: "Google Sheets", color: "text-green-600" },
  "application/vnd.google-apps.presentation": { icon: "AlignLeft", label: "Google Slides", color: "text-orange-600" },
  "video/mp4": { icon: "Play", label: "MP4 Video", color: "text-purple-600" },
  "video/x-matroska": { icon: "Play", label: "MKV Video", color: "text-purple-600" },
  "video/webm": { icon: "Play", label: "WebM Video", color: "text-purple-600" },
} as const;
